import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { Button } from "reactstrap";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as PersonnelActions from "../../../actions/personnel";
import DropdownButton, { IDropdownOption } from "../../../components/common/Buttons/DropdownButton";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import {
    CheckboxFormInput,
    DatePickerFormInputFabric,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { GridRemove } from "../../../enumerations/GridRemove";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { PersonnelCreateBreadcrumbs, PersonnelDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    getFormPropertyLabel,
    renderFormControls,
} from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { PersonnelSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { IPersonnelReduxFormState } from "../../../reducers/reactReduxForms/personnel";

import "./PersonnelPage.scss";

interface PersonnelPageProps {
    form: Dtos.Personnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IPersonnelReduxFormState;
    personnel: Dtos.Personnel;
    lookups: Dtos.Lookup[];
    loadingPersonnel: boolean;
    loadPersonnelSuccess: boolean;
    loadPersonnelFailure: boolean;
    loadPersonnelErrorCode: string;
    savingPersonnel: boolean;
    savePersonnelSuccess: boolean;
    savePersonnelFailure: boolean;
    creatingPersonnel: boolean;
    createPersonnelSuccess: boolean;
    createPersonnelFailure: boolean;
    validatingPersonnel: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    loadPersonnel: PersonnelActions.IPersonnelLoadByIdActionCreator;
    savePersonnel: PersonnelActions.IPersonnelSaveActionCreator;
    createPersonnel: PersonnelActions.IPersonnelCreateActionCreator;
    validatePersonnel: PersonnelActions.IPersonnelValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.personnel";

const PersonnelPage: FC<PersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    //reduxFormState,
    personnel,
    lookups,
    loadingPersonnel,
    //loadPersonnelSuccess,
    loadPersonnelFailure,
    loadPersonnelErrorCode,
    savingPersonnel,
    savePersonnelSuccess,
    savePersonnelFailure,
    creatingPersonnel,
    //createPersonnelSuccess,
    //createPersonnelFailure,
    validatingPersonnel,
    validationFailures,
    permissions,
    clearPersonnel,
    loadPersonnel,
    savePersonnel,
    createPersonnel,
    validatePersonnel,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { personnelId } = useParams<{ personnelId: string }>();
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Personnel = {} as Dtos.Personnel;

        loadForm(reduxFormName, Object.assign({ ...personnel }, extraData));
    };


    // Load personnel
    useEffect(() => {
        if (personnelIdNumber && !personnel && !loadingPersonnel) {
            setDrawerItems(PersonnelSubMenu(personnelIdNumber, permissions));
            setBreadcrumbs(PersonnelDetailsBreadcrumbs(personnelIdNumber));
            loadPersonnel(personnelIdNumber);
        } else if (!personnelIdNumber && !personnel && !creatingPersonnel) {
            createPersonnel();
            setBreadcrumbs(PersonnelCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (personnel && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        personnelIdNumber,
        personnel,
        loadingPersonnel,
        permissions,
        creatingPersonnel,
        initalised
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelFailure) {
            navigate(`/error/${loadPersonnelErrorCode}`);
        }
    }, [loadPersonnelFailure, loadPersonnelErrorCode]);

    // Update breadcrumbs
    useEffect(() => {
        if (personnelIdNumber && personnel) {
            setDrawerItems(
                PersonnelSubMenu(
                    personnelIdNumber,
                    permissions,
                    personnel?.fellowships
                )
            );
            setBreadcrumbs(
                PersonnelDetailsBreadcrumbs(
                    personnelIdNumber,
                    personnel?.displayWithSalutation
                )
            );
        }
    }, [personnelIdNumber, personnel, permissions]);

    // Reload personnel after saving
    useEffect(() => {
        if (!savingPersonnel && savePersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate("/personnel");
            } 
            else if (!personnelIdNumber) {
                navigate(`/personnel/${personnel?.id}/details`);
                clearPersonnel();
            }
        }
    }, [
        savingPersonnel,
        savePersonnelSuccess,
        saveAndReturn,
        personnel,
        personnelIdNumber
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingPersonnel && savePersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingPersonnel, savePersonnelFailure]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearPersonnel();
            resetForm(reduxFormName);
        };
    }, [clearPersonnel, resetForm, reduxFormName]);

    const onCancel = () => {
        navigate("/personnel");
    };

    const handleOnSubmitFailed = (_data: Dtos.Personnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Personnel = { trainingAndCertifications: false } as Dtos.Personnel;

            if (data.personnelInterests?.length > 0) {
                changes.personnelInterests = 
                    data.personnelInterests.map((x) => 
                        ({ 
                            personnelId: personnelIdNumber, 
                            interestId: x.interestId ?? x.value, 
                            active: true 
                        })
                    );
            }

            await savePersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Personnel = {} as Dtos.Personnel;

            if (form && hasBeenSubmitted && !validatingPersonnel) {
                await validatePersonnel(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const hideFunction = (form: any, _: string): boolean => {
        if (form.addressTypeId != 2) {
            return true;
        }
        return false;
    }

    const openFile = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personalAssistant 
                ? { label: form?.personalAssistant?.displayWithSalutation, value: form?.personalAssistantId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const selectedPersonnelInterests = useMemo(() => {
        return form.personnelInterests?.map((x: any) => 
            ({ 
                label: x?.interest?.value ?? x?.label, 
                value: x?.interestId ?? x?.value 
            })
        ) ?? [];
    }, [form.personnelInterests]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.PersonnelUpdate,
                Dtos.Permission.PersonnelCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            salutationId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "personnel",
                                                                "salutationId",
                                                                lookups
                                                            ),
                                                            form.salutationId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            firstName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            lastName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2 pt-lg-0",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            userName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: true,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            personalAssistantId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personalAssistantId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personalAssistant
                                                        ? [{ label: form.personalAssistant.displayWithSalutation, value: form.personalAssistant.id } as Dtos.PersonnelOption]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2 pt-lg-0",
                                                labelClassName: "col-form-label col-sm-2 col-lg-3",
                                                fieldClassName: "col-sm-10 col-lg-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            contactDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Contact Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            personnelAddresses: {
                                                type: RegistrationComponent.RegistrationGrid,
                                                formName: "PersonnelAddress",
                                                disabled: formDisabled,
                                                className: "source-documents-source-documents",
                                                minRows: 0,
                                                columnStyles: [
                                                    {
                                                        width: undefined,
                                                        minWidth: "200px",
                                                    },
                                                    {
                                                        width: "125px",
                                                    },
                                                ],
                                                removeConfig: GridRemove.All,
                                                addRow: (_gridComponent, _index, _array) => {
                                                    const state: Dtos.PersonnelAddress = Object.assign({});
                                                    return state;
                                                },
                                                components: {
                                                    Address: {
                                                        type: RegistrationComponent.RegistrationGroupFormControls,
                                                        groupClassName: "row",
                                                        components: {
                                                            addressTypeId: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: SelectFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputProps: {
                                                                    options:
                                                                        generateOptionsFromLookup(
                                                                            findLookup(
                                                                                "Personnel",
                                                                                "PersonnelAddresses",
                                                                                lookups
                                                                            ),
                                                                            undefined
                                                                        ),
                                                                },
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "Address Type",
                                                                        formProperties
                                                                    ),
                                                                size: 1,
                                                            },
                                                            companyName: {
                                                                hideFn: hideFunction,
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "CompanyName",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            companyDepartment: {
                                                                hideFn: hideFunction,
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "CompanyDepartment",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            addressLine1: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "AddressLine1",
                                                                        formProperties
                                                                    ),
                                                                size: 1,
                                                            },
                                                            addressLine2: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "AddressLine2",
                                                                        formProperties
                                                                    ),
                                                                size: 1,
                                                            },
                                                            suburbCity: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "SuburbCity",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            zipPostalCode: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "ZIPPostalCode",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            stateProvinceRegionId:
                                                                {
                                                                    type: RegistrationComponent.RegistrationControl,
                                                                    inputType: SelectFormInput,
                                                                    inputDisabled: formDisabled,
                                                                    inputProps:
                                                                        {
                                                                            options:
                                                                                generateOptionsFromLookup(
                                                                                    findLookup(
                                                                                        "PersonnelAddress",
                                                                                        "StateProvinceRegionId",
                                                                                        lookups
                                                                                    ),
                                                                                    null
                                                                                ),
                                                                        },
                                                                    inputLabel:
                                                                        getFormPropertyLabel(
                                                                            "PersonnelAddress",
                                                                            "State/Province/Region",
                                                                            formProperties
                                                                        ),
                                                                    changeAction: (model, value, _form, parentModel) => {
                                                                        changeForm(model, value);
                                                                        changeForm(reduxFormName + parentModel + ".state", null);
                                                                        if (value == null || value != 10) {
                                                                            changeForm(
                                                                                reduxFormName + parentModel + ".stateOther",
                                                                                null
                                                                            );
                                                                        }
                                                                    },
                                                                    size: 2,
                                                                },
                                                            stateOther: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "StateOther",
                                                                        formProperties
                                                                    ),
                                                                disabledFn:
                                                                    formDisabled
                                                                        ? undefined
                                                                        : (form, _key) => {
                                                                            return (
                                                                                (form && form.stateProvinceRegionId == null) ||
                                                                                (form && form.stateProvinceRegionId != 10)
                                                                            );
                                                                        },
                                                                size: 2,
                                                            },
                                                            countryId: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: SelectFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputProps: {
                                                                    options:
                                                                        generateOptionsFromLookup(
                                                                            findLookup(
                                                                                "PersonnelAddress",
                                                                                "CountryId",
                                                                                lookups
                                                                            ),
                                                                            undefined
                                                                        ),
                                                                },
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "Country",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            spacer: {
                                                                type: RegistrationComponent.RegistrationElement,
                                                                component: (
                                                                    <br />
                                                                ),
                                                            },
                                                            primaryEmail: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "PrimaryEmail",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            groupEmail: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "GroupEmail",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            mobilePhone: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "MobilePhone",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                            fixedPhone: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel:
                                                                    getFormPropertyLabel(
                                                                        "PersonnelAddress",
                                                                        "FixedPhone",
                                                                        formProperties
                                                                    ),
                                                                size: 2,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            professionalGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Professional Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            jobTitle: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            cvDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2 pt-lg-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            electronicCV: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2",
                                                fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <Button
                                                            type="button"
                                                            onClick={() => {
                                                                openFile(form.electronicCV);
                                                            }}
                                                            disabled={form?.electronicCV == null || !isUrl(form?.electronicCV)}
                                                            style={{
                                                                cursor: form?.electronicCV == null || !isUrl(form?.electronicCV)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </Button>
                                                    ),
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            cvNoChangeConfirmedDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            cvRequestSentDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2 pt-lg-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            professionalDisciplineId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "personnel",
                                                                "professionalDisciplineId",
                                                                lookups
                                                            ),
                                                            form.professionalDisciplineId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            otherDisciplines: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2 pt-lg-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2 col-lg-4",
                                                fieldClassName: "col-sm-12 col-md-10 col-lg-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            personnelInterests: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    multi: true,
                                                    value: selectedPersonnelInterests,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Personnel",
                                                                "PersonnelInterests",
                                                                lookups
                                                            )
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2",
                                                fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            otherInterests: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2",
                                                fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            commentsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Comments</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-3 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "personnel",
                                        {
                                            comments: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-0",
                                                fieldClassName: "col-sm-12 generic-form-field",
                                                inputLabel: " ",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <div className="title-bar">
                    <h1>Personnel</h1>
                    {personnelIdNumber && (
                        <DropdownButton
                            title={"Create"}
                            options={[
                                {
                                    title: "Fellowship",
                                    url: `/personnel/${personnelIdNumber}/fellowship/create`,
                                } as IDropdownOption,
                            ]}
                        />
                    )}
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "personnel",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        form,
        permissions,
        savingPersonnel,
        formState,
        generateOptionsFromLookup,
        findLookup,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        renderFormControls,
        openFile,
        personnelIdNumber,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        onCancel,
        personnelSearchLoader,
        selectedPersonnelInterests
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelView}
            loading={loadingPersonnel || creatingPersonnel}
        >
            { renderContent }
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<PersonnelPageProps> => {
    let personnel: Dtos.Personnel | undefined = !(state.personnel.formData instanceof Array)
        ? state.personnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        personnel: personnel!,
        loadingPersonnel: state.personnel.loadState && state.personnel.loadState.status == RequestState.Pending,
        loadPersonnelSuccess: state.personnel.loadState && state.personnel.loadState.status == RequestState.Success,
        loadPersonnelFailure: state.personnel.loadState && state.personnel.loadState.status == RequestState.Failure,
        loadPersonnelErrorCode: state.personnel.loadState.errorCode,
        savingPersonnel: state.personnel.saveState && state.personnel.saveState.status == RequestState.Pending,
        savePersonnelSuccess: state.personnel.saveState && state.personnel.saveState.status == RequestState.Success,
        savePersonnelFailure: state.personnel.saveState && state.personnel.saveState.status == RequestState.Failure,
        creatingPersonnel: state.personnel.createState && state.personnel.createState.status == RequestState.Pending,
        createPersonnelSuccess: state.personnel.createState && state.personnel.createState.status == RequestState.Success,
        createPersonnelFailure: state.personnel.createState && state.personnel.createState.status == RequestState.Failure,
        validatingPersonnel: state.personnel.validateState && state.personnel.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.personnel.lookups,
        form: state.reduxForms.personnel,
        formState: state.personnel.formState,
        formProperties: state.personnel.formProperties,
        reduxFormState: state.reduxForms.formState.personnel,
        validationFailures: personnel
            ? state.personnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<PersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadPersonnel: bindActionCreators(PersonnelActions.LoadPersonnelById, dispatch),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
        savePersonnel: bindActionCreators(PersonnelActions.SavePersonnel, dispatch),
        createPersonnel: bindActionCreators(PersonnelActions.CreatePersonnel, dispatch),
        validatePersonnel: bindActionCreators(PersonnelActions.ValidatePersonnel, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelPage);
